import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import {
  fetchForecastYears,
  fetchPublicSettings,
  openForecastYear,
  patchPublicSettings,
  updateForecastYear,
} from '@/api/client'
import { ApiError } from '@/api/client'

import { Calendar } from 'lucide-react'
import { AdminSection } from '@/features/admin/AdminSection'

const MONTH_OPTIONS = [
  { value: 1, label: 'January' },
  { value: 2, label: 'February' },
  { value: 3, label: 'March' },
  { value: 4, label: 'April' },
  { value: 5, label: 'May' },
  { value: 6, label: 'June' },
  { value: 7, label: 'July' },
  { value: 8, label: 'August' },
  { value: 9, label: 'September' },
  { value: 10, label: 'October' },
  { value: 11, label: 'November' },
  { value: 12, label: 'December' },
]

export function YearsAdminPage() {
  const qc = useQueryClient()
  const yearsQuery = useQuery({
    queryKey: ['config', 'years', 'admin'],
    queryFn: () => fetchForecastYears({ openOnly: false }),
  })
  const settingsQuery = useQuery({
    queryKey: ['config', 'settings'],
    queryFn: fetchPublicSettings,
  })

  const [error, setError] = useState<string | null>(null)
  const [newYear, setNewYear] = useState(String(new Date().getFullYear() + 1))
  const [yearStartMonth, setYearStartMonth] = useState(1)

  useEffect(() => {
    const raw = settingsQuery.data?.settings?.year_start_month
    if (typeof raw === 'number' && raw >= 1 && raw <= 12) {
      setYearStartMonth(raw)
    }
  }, [settingsQuery.data])

  const openMut = useMutation({
    mutationFn: openForecastYear,
    onSuccess: async () => {
      setError(null)
      await qc.invalidateQueries({ queryKey: ['config', 'years'] })
    },
    onError: (err: unknown) => {
      setError(err instanceof ApiError ? err.message : 'Failed to open year')
    },
  })

  const patchYearMut = useMutation({
    mutationFn: ({ id, is_open }: { id: string; is_open: boolean }) =>
      updateForecastYear(id, { is_open }),
    onSuccess: async () => {
      setError(null)
      await qc.invalidateQueries({ queryKey: ['config', 'years'] })
    },
    onError: (err: unknown) => {
      setError(err instanceof ApiError ? err.message : 'Failed to update year')
    },
  })

  const settingsMut = useMutation({
    mutationFn: (settings: Record<string, unknown>) => patchPublicSettings(settings),
    onSuccess: async () => {
      setError(null)
      await qc.invalidateQueries({ queryKey: ['config', 'settings'] })
    },
    onError: (err: unknown) => {
      setError(err instanceof ApiError ? err.message : 'Failed to save settings')
    },
  })

  const years = yearsQuery.data ?? []

  return (
    <AdminSection
      title="Forecast years"
      tone="teal"
      icon={Calendar}
      lede="Open forecast years for planning and set the fiscal year start month."
    >
      {error && (
        <p className="alert alert--error" role="alert">
          {error}
        </p>
      )}

      <div className="table-wrap">
        <table className="table table--dense">
          <thead>
            <tr>
              <th>Year</th>
              <th>Status</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {years.map((y) => {
              const open = y.is_open ?? y.open ?? false
              const id = y.id
              return (
                <tr key={y.id ?? y.year}>
                  <td>{y.year}</td>
                  <td>
                    <span className={open ? 'badge badge--status-on' : 'badge badge--status-off'}>
                      {open ? 'Open' : 'Closed'}
                    </span>
                  </td>
                  <td className="table__actions">
                    {id ? (
                      <button
                        type="button"
                        className="button button--ghost"
                        disabled={patchYearMut.isPending}
                        onClick={() => patchYearMut.mutate({ id, is_open: !open })}
                      >
                        {open ? 'Close year' : 'Reopen year'}
                      </button>
                    ) : (
                      <span className="admin-section__hint">Missing id — refresh config</span>
                    )}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      <form
        className="admin-form"
        onSubmit={(event) => {
          event.preventDefault()
          openMut.mutate({ year: Number(newYear), is_open: true })
        }}
      >
        <h3 className="admin-form__title">Open a year</h3>
        <div className="admin-form__row">
          <label className="field">
            <span className="field__label">Calendar year</span>
            <input
              className="field__input"
              type="number"
              min={2000}
              max={2100}
              value={newYear}
              onChange={(e) => setNewYear(e.target.value)}
              required
            />
          </label>
          <button className="button button--primary" type="submit" disabled={openMut.isPending}>
            Open year
          </button>
        </div>
      </form>

      <form
        className="admin-form"
        onSubmit={(event) => {
          event.preventDefault()
          settingsMut.mutate({ year_start_month: yearStartMonth })
        }}
      >
        <h3 className="admin-form__title">Year start month</h3>
        <p className="admin-section__lede">
          Defines how forecast year boundaries align across totals and exports.
        </p>
        <div className="admin-form__row">
          <label className="field">
            <span className="field__label">Start month</span>
            <select
              className="field__input"
              value={yearStartMonth}
              onChange={(e) => setYearStartMonth(Number(e.target.value))}
            >
              {MONTH_OPTIONS.map((m) => (
                <option key={m.value} value={m.value}>
                  {m.label}
                </option>
              ))}
            </select>
          </label>
          <button className="button button--primary" type="submit" disabled={settingsMut.isPending}>
            Save start month
          </button>
        </div>
      </form>
    </AdminSection>
  )
}
